import { memo } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { eventTypeConvert } from '@/shared/lib/aiUtils';
import { ThumbnailEvent } from '@/entities/event';
import useEventPreviewStrip from './useEventPreviewStrip';

export type EventPreviewStripProps = {
  width: number;
  height: number;
  customOffset: number[];
  event: any;
  ecgDataMap: any;
  overlayContent?: any;
  id?: string;
  isSelected?: boolean;
  isActive?: boolean;
  channels?: any;
  highPass?: any;
  gain?: any;
  timezoneOffset?: any;
  startReport?: any;
  index?: number;
  onClickSelectEvent?: (event: any, index: any) => void;
};

/**
 * Renders one artifact-area event as an ECG thumbnail with its edit and capture indicators. Pulls ECG samples, beat markers and overlay content from useEventPreviewStrip, prefers the pending edit over the stored event for start, duration and type, and appends a tooltip warning while the change has not been applied.
 *
 * @param props - Strip props with the event, its display size and the ECG rendering settings.
 * @returns The rendered event thumbnail, plus an unapplied-change tooltip when edited.
 */
const EventPreviewStrip = ({
  width,
  height,
  customOffset,
  event,
  ecgDataMap,
  overlayContent: overlayContentProp,
  id,
  isSelected,
  isActive,
  channels: channelsProp,
  highPass,
  gain,
  timezoneOffset,
  startReport,
  index,
  onClickSelectEvent,
}: EventPreviewStripProps) => {
  const {
    overlayContent,
    ecgData,
    beatMarkers,
    hesBeatStatus,
    eleChange,
    onClick,
    isEdited,
    isNoHrEvent,
    channels,
  } = useEventPreviewStrip({
    overlayContent: overlayContentProp,
    event,
    ecgDataMap,
    customOffset,
    index,
    channels: channelsProp,
    onClickSelectEvent,
  });

  return (
    <>
      <ThumbnailEvent
        id={id}
        isNoHrEvent={isNoHrEvent}
        isBeatEvent={false}
        overlayContent={overlayContent}
        onClickSelect={onClick as any}
        isSelected={isSelected}
        isActive={isActive}
        isCaptured={eleChange ? eleChange.stripImgs?.length > 0 : event.stripImgs?.length > 0}
        isChanged={isEdited}
        ecgData={ecgData}
        beatMarkers={beatMarkers}
        hesBeatStatus={hesBeatStatus}
        channels={channels}
        highPass={highPass}
        width={width}
        height={height}
        gain={gain}
        time={eleChange?.start || event.start}
        duration={eleChange?.duration || event.duration}
        typeRight={eventTypeConvert[eleChange?.type]}
        timezoneOffset={timezoneOffset}
        startReport={startReport}
      />
      {isEdited && id && (
        <UncontrolledTooltip
          className="custom-tooltip custom-event-beat-tooltip"
          target={id}
          modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          The change has not been applied
        </UncontrolledTooltip>
      )}
    </>
  );
};

export default memo(EventPreviewStrip);
